import classNames from 'classnames';
import { forwardRef, useContext } from 'react';
import { isFeatureActive } from '@helpers';
import { Button } from 'palette/Button';
import { ConfigContext } from 'palette/Config';
import { Svg } from 'palette/Svg';
import { BodyText } from 'palette/Typography';
import { BurgerNavSearch } from '@common/BurgerNavigation/BurgerNavSearch';
import { CurrentLanguageIndicator } from '@common/BurgerNavigation/CurrentLanguageIndicator';
import { CurrentStoreIndicator } from '@common/BurgerNavigation/CurrentStoreIndicator';
import { useConfig } from '@common/Hooks/useConfig';
import { isBooztmobile, isBooztletAndWebshop } from '@helpers/env';
import { getSlidePanelHeaderLogoName } from '../helpers/getSlidePanelHeaderLogoName';

import '../styles/SlidePanelHeader.scss';

const SlidePanelHeader = forwardRef(({
    onBackClick,
    onCloseClick,
    title,
    subtitle,
    icon,
    hasCloseBtn = true,
    hasStoreSelect,
    hasLanguageSelect,
    hasLogo,
    theme = undefined,
    customClass = undefined,
    onStoreSelectClick,
    onLanguageSelectClick,
    hasHeaderSearch,
    uuids,
    tabIds,
    unisexTabIds,
    isCountrySelect
}, ref) => {
    const config = useConfig();
    const isMobileMenuRefresh = isFeatureActive('PLT-34079_mobile_nav_refresh_2023', config);
    const hasBackBtnHandler = typeof onBackClick === 'function';
    const shopLogo = getSlidePanelHeaderLogoName(isBooztletAndWebshop(config), config.store);
    const isLanguageSelectActive = isFeatureActive('PLT-26652-add-lang-select-desktop', config);
    const { svgPath } = useContext(ConfigContext);
    return (
        <div
            ref={ref}
            className={classNames('slide-panel-header', { [`slide-panel-header--${theme}`]: theme }, customClass)}
        >
            <div className="slide-panel-header__left-col">
                {hasHeaderSearch
                    && (
                        <BurgerNavSearch
                            tabIds={tabIds}
                            theme={theme}
                            unisexTabIds={unisexTabIds}
                            uuids={uuids}
                        />
                    )}
                {hasStoreSelect && (
                    <CurrentStoreIndicator
                        customClass="slide-panel-header__store"
                        onClick={onStoreSelectClick}
                    />
                )}
                {
                    hasLanguageSelect
                    && isLanguageSelectActive
                    && isBooztmobile()
                    && !isMobileMenuRefresh
                    && <CurrentLanguageIndicator onClick={onLanguageSelectClick} />
                }
                {hasBackBtnHandler && (!isMobileMenuRefresh || isCountrySelect) && (
                    <Button
                        isTransparent
                        iconName="palette_chevron_left"
                        size="large"
                        theme="secondary"
                        onClick={onBackClick}
                    />
                )}
            </div>
            <div className="slide-panel-header__content">
                {
                    title && !isMobileMenuRefresh
                    && (
                        <div className="slide-panel-header__title">
                            <BodyText
                                color="strong"
                                level="2"
                            >
                                {title}
                            </BodyText>
                        </div>
                    )
                }
                {subtitle && <span className="slide-panel-header__subtitle">{subtitle}</span>}
                {hasLogo
                    && (
                        <a href="/">
                            <Svg
                                className="slide-panel-header__logo"
                                name={shopLogo}
                                svgUrl={svgPath}
                            />
                        </a>
                    )}
            </div>
            <div className="slide-panel-header__right-col">
                {hasCloseBtn && (
                    <Button
                        isTransparent
                        ariaLabel="Exit"
                        iconName={icon}
                        size="large"
                        theme="secondary"
                        onClick={onCloseClick}
                    />
                )}
            </div>
        </div>
    );
});

export default SlidePanelHeader;
